import type { GetNotificationSettings } from "@/server-types";

type NotificationSettingListItem =
  GetNotificationSettings["response"]["settings"][number];

type SettingMatch = {
  event?: NonNullable<NotificationSettingListItem["event"]>;
  trigger?: NonNullable<NotificationSettingListItem["trigger"]>;
};

/**
 * Looks up the enabled setting behind an appointment event, matching either
 * the event-based (`event`) or the legacy trigger-based (`trigger`) column —
 * a tenant can be on either side of the variant migration.
 *
 * `settings` is typed as always present, but a `/notification/setting`
 * response that doesn't conform (version-skewed server, an error body served
 * with 200) omits it, and dereferencing it took the whole calendar down with
 * it. A missing list means "nothing configured", never a crash — the caller
 * only uses the result to decorate a toggle with channel icons.
 */
export function findEnabledSetting(
  settings: NotificationSettingListItem[] | undefined,
  match: SettingMatch,
): NotificationSettingListItem | undefined {
  if (!Array.isArray(settings)) return undefined;

  return settings.find(
    (setting) =>
      setting.isEnabled &&
      ((match.event != null && setting.event === match.event) ||
        (match.trigger != null && setting.trigger === match.trigger)),
  );
}
